'use client';

import Stock from '@app/_components/Stock/Stock';
import { isProductOversellable } from '@app/_components/Stock/Stock.utils';
import WishlistButton from '@app/_pages/product/components/productProperties/WishlistButton';
import Link from '@components/Link';
import { Button } from '@components/ui/Button';
import { useCartActions, useCartState } from '@context/cartContext';
import useSignUp from '@hooks/useSignUp';
import { Quantity } from '@ngg/components';
import { CheckIcon } from '@ngg/icons';
import type {
    ProductPageInfoBlockFragment,
    ProductPageInfoFragment,
} from '@server/gql/graphql';
import { getStockWatch } from '@server/requests/checkout/stockWatch';
import { useQuery } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import type { FormEvent } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Dictionary } from 'types/common';
import type { Product, Variant } from 'types/product';

import { cn } from '@/lib/utils';
import { isSoonBackInStock } from '@/utils/product/isSoonBackInStock';

export default function ProductPageCart({
    variantIds,
    dictionary,
    lightsource,
    product,
    variant,
    className,
    stockInfo,
    locale,
}: {
    variantIds: string[];
    dictionary: Dictionary;
    lightsource?: Variant | null;
    product: Product;
    variant?: Variant;
    className?: string;
    stockInfo?: ProductPageInfoBlockFragment | null;
    locale?: string;
    madeToOrderBlock?: ProductPageInfoFragment['madeToOrderInfo'];
}) {
    const [quantity, setQuantity] = useState(1);
    const { cart, loading, error } = useCartState();
    const { addItem } = useCartActions();
    const [recentlyAdded, setRecentlyAdded] = useState<boolean>(false);
    const [optimisticallyAdded, setOptimisticallyAdded] =
        useState<boolean>(false);
    const [addedToCart, setAddedToCart] = useState(false);

    /**
     * We won't have stock data if item is oversellable and we don't care
     * about how many items are in the cart
     */
    const { data: stockData } = useQuery({
        queryKey: ['stockWatch', product.id],
        queryFn: () =>
            getStockWatch({
                items: variantIds.map((partNo) => ({ partNo, quantity: 1000 })),
            }),
        enabled: !isProductOversellable(product),
    });

    const isDisableAddQuantity = useMemo(() => {
        if (!stockData) {
            return false;
        }

        const cartItem = cart?.items.find(
            (item) => item.partNo === variant?.id,
        );
        const stockWatch = stockData?.find(
            (data) => data.partNo === variant?.id,
        );

        if (!stockWatch) {
            return false;
        }

        const currentQuantity = (cartItem?.quantity ?? 0) + quantity;

        return currentQuantity >= stockWatch.currentOnHand;
    }, [cart?.items, quantity, variant, stockData]);

    const isDisabled = useMemo(() => {
        if (!variant?.id) {
            return true;
        }
        if (!variant.articleStatus) {
            return false;
        }

        const cartItem = cart?.items.find(
            (item) => item.partNo === variant?.id,
        );

        if (!cartItem || !stockData) {
            return false;
        }

        const stockWatch = stockData.find((d) => d.partNo === variant?.id);

        if (!stockWatch) {
            return false;
        }

        return cartItem.quantity >= stockWatch.currentOnHand;
    }, [stockData, cart?.items, variant]);

    const handleDecreaseQuantity = () =>
        setQuantity((prev) => Math.max(1, prev - 1));

    const handleIncreaseQuantity = () => {
        if (isDisableAddQuantity) {
            return;
        }

        setQuantity((prev) => prev + 1);
    };

    useEffect(() => {
        setAddedToCart(false);
    }, [variant]);

    const {
        active,
        toggleNotification,
        loading: loadingNotification,
    } = useSignUp({
        productId: product.id,
        variantId: variant?.id,
    });

    const shouldBeMonitored = useMemo(() => {
        if (
            variant?.stockStatus &&
            ['SoldOut', 'OutOfStock'].includes(variant.stockStatus) &&
            !isSoonBackInStock(variant.stockStatus, variant.nextDeliveryDate)
        ) {
            return true;
        }

        return false;
    }, [variant]);

    const handleSubmit = useCallback(
        async (event: FormEvent) => {
            event.preventDefault();

            if (!variant?.id) {
                return;
            }

            setOptimisticallyAdded(true);

            try {
                await addItem([
                    {
                        product,
                        quantity,
                        item: variant,
                    },
                    ...(lightsource
                        ? [
                              {
                                  product,
                                  item: lightsource,
                                  quantity:
                                      (variant.lightsourceQuantity ?? 1) *
                                      quantity,
                              },
                          ]
                        : []),
                ]);

                setAddedToCart(true);
                setRecentlyAdded(true);
                setOptimisticallyAdded(false);

                setTimeout(() => setRecentlyAdded(false), 3000);
            } catch (error) {
                console.error(error);
                setOptimisticallyAdded(false);
            }
        },
        [addItem, quantity, variant, lightsource, product],
    );

    return (
        <div className={cn('grid grid-cols-2 gap-y-4', className)}>
            <form
                onSubmit={handleSubmit}
                className={cn(
                    'col-span-2 grid gap-x-2 gap-y-4 xl:gap-4',
                    !shouldBeMonitored &&
                        'grid-cols-[min-content_auto_min-content]',
                )}>
                {shouldBeMonitored ? (
                    <div className="flex gap-2">
                        <Button
                            type="button"
                            isLoading={loadingNotification}
                            disabled={loadingNotification}
                            onClick={toggleNotification}
                            className={cn(
                                'btn-primary col-span-3 w-full',
                                active && 'border-0 bg-green-600 text-white',
                            )}>
                            {active
                                ? dictionary.removeNotification
                                : dictionary.monitorProduct}
                        </Button>
                        <WishlistButton
                            productId={product.id}
                            variantId={
                                variant?.id !== product.id ? variant?.id : ''
                            }
                        />
                    </div>
                ) : (
                    <>
                        <Quantity
                            readOnly
                            onDecrease={handleDecreaseQuantity}
                            onIncrease={handleIncreaseQuantity}
                            value={quantity}
                            classNames={{
                                wrapper: 'border-0 h-10 lg:h-12',
                                input: 'border-black border-y text-sm min-w-0 w-10 lg:w-12 -outline-offset-2 action-focus rounded-none font-medium',
                                decreaseButton:
                                    'action-focus border-black border basis-10 lg:basis-12 -outline-offset-2 w-10 lg:w-12 flex justify-center items-center',
                                increaseButton: cn([
                                    'action-focus flex w-10 basis-10 items-center justify-center border border-black -outline-offset-2 lg:w-12 lg:basis-12',
                                    isDisableAddQuantity &&
                                        'cursor-not-allowed opacity-50',
                                ]),
                            }}
                        />
                        <Button
                            type="submit"
                            disabled={!variant || isDisabled}
                            isLoading={
                                !optimisticallyAdded &&
                                (loading.addToCart || loading.createCart)
                            }
                            variant="primary"
                            className={cn(
                                (recentlyAdded || optimisticallyAdded) &&
                                    'border-green pointer-events-none bg-green-600',
                                (loading.createCart || loading.addToCart) &&
                                    'pointer-events-none',
                            )}
                            data-testid="PDP-button-Add-to-Cart">
                            {recentlyAdded || optimisticallyAdded ? (
                                <CheckIcon className="text-xl" />
                            ) : (
                                <span>{dictionary.addToCart}</span>
                            )}
                        </Button>
                        <WishlistButton
                            productId={product.id}
                            variantId={
                                variant?.id !== product.id ? variant?.id : ''
                            }
                        />
                        {error.createCart || error.addToCart ? (
                            <p className="col-span-3 mt-1 text-xxs text-error md:col-span-1 md:col-start-2 md:pl-4">
                                {dictionary.errorAddToCart}
                            </p>
                        ) : null}
                        <AnimatePresence>
                            {addedToCart && (
                                <motion.div
                                    className="col-span-full overflow-hidden"
                                    initial={{ height: 0 }}
                                    animate={{ height: 'auto' }}>
                                    <Button asChild>
                                        <Link
                                            locale={locale}
                                            className="min-h-10 w-full bg-black p-2 text-sm text-white lg:min-h-12 lg:text-base"
                                            href="/checkout">
                                            {dictionary.goToCheckout}
                                        </Link>
                                    </Button>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </>
                )}
            </form>
            {variant && (
                <Stock
                    item={variant}
                    type="pdp"
                    dictionary={dictionary}
                    className="col-span-2"
                    stockInfo={stockInfo}
                />
            )}
        </div>
    );
}
